import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { firebaseApp } from "../../firebase";
import {
  getFirestore,
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
} from "firebase/firestore";
import { useAuth } from "../../contexts/AuthContext";
import { Briefcase, MapPin, Grid3x3, List, Filter, Calendar } from "lucide-react";

interface Job {
  id: string;
  jobTitle: string;
  jobPosition: string;
  education: string;
  experience: number;
  createdAt: any;
}

const formatDate = (timestamp: any) => {
  if (!timestamp) return "N/A";
  return new Date(timestamp.seconds * 1000).toLocaleDateString();
};

const MyJobs: React.FC = () => {
  const { user } = useAuth();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [filteredJobs, setFilteredJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");
  const [dateFilter, setDateFilter] = useState<"all" | "today" | "week" | "month">("all");

  useEffect(() => {
    if (!user) return;

    const db = getFirestore(firebaseApp);
    const q = query(
      collection(db, "hrJobs"),
      where("hrId", "==", user.id),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const jobList = snapshot.docs.map(
        (doc) => ({ id: doc.id, ...doc.data() } as Job)
      );
      setJobs(jobList);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  // Filter jobs based on date
  useEffect(() => {
    const now = new Date();
    let filtered = [...jobs];

    if (dateFilter === "today") {
      filtered = jobs.filter((job) => {
        const jobDate = new Date(job.createdAt.seconds * 1000);
        return jobDate.toDateString() === now.toDateString();
      });
    } else if (dateFilter === "week") {
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      filtered = jobs.filter((job) => {
        const jobDate = new Date(job.createdAt.seconds * 1000);
        return jobDate >= weekAgo;
      });
    } else if (dateFilter === "month") {
      const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      filtered = jobs.filter((job) => {
        const jobDate = new Date(job.createdAt.seconds * 1000);
        return jobDate >= monthAgo;
      });
    }

    setFilteredJobs(filtered);
  }, [jobs, dateFilter]);

  if (loading) {
    return <div className="text-center py-10">Loading your jobs...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header with Title and Actions */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">My Posted Jobs</h1>
        <Link
          to="/hr/jd-upload"
          className="px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 transition-colors"
        >
          Post New Job
        </Link>
      </div>

      {/* Filters and View Toggle */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          {/* Date Filter */}
          <div className="flex items-center gap-3">
            <Filter className="w-5 h-5 text-gray-600" />
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
            </select>
            <span className="text-sm text-gray-600">
              {filteredJobs.length} job{filteredJobs.length !== 1 ? "s" : ""} found
            </span>
          </div>

          {/* View Toggle */}
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode("list")}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === "list"
                  ? "bg-white text-red-800 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
              title="List View"
            >
              <List className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode("grid")}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === "grid"
                  ? "bg-white text-red-800 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
              title="Grid View"
            >
              <Grid3x3 className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Jobs Display */}
      {filteredJobs.length === 0 ? (
        <div className="text-center py-20 bg-white border rounded-xl">
          <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-2">
            {jobs.length === 0
              ? "You haven't posted any jobs yet."
              : "No jobs found for the selected filter."}
          </p>
          {jobs.length === 0 && (
            <Link
              to="/hr/jd-upload"
              className="text-red-700 hover:underline mt-2 inline-block"
            >
              Post a new job
            </Link>
          )}
        </div>
      ) : viewMode === "list" ? (
        <div className="space-y-4">
          {filteredJobs.map((job) => (
            <Link
              to={`/hr/my-jobs/${job.id}`}
              key={job.id}
              className="block bg-white p-6 border rounded-lg shadow-sm hover:bg-gray-50"
            >
              <div className="flex justify-between items-start">
                <h2 className="text-xl font-semibold text-gray-900">
                  {job.jobTitle}
                </h2>
                <span className="text-sm text-gray-500">
                  Posted on {formatDate(job.createdAt)}
                </span>
              </div>
              <div className="flex items-center space-x-4 text-sm text-gray-600 mt-2">
                <div className="flex items-center capitalize">
                  <Briefcase className="w-4 h-4 mr-1" />
                  {job.jobPosition}
                </div>
                <div className="flex items-center">
                  <MapPin className="w-4 h-4 mr-1" />
                  {job.experience} years
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredJobs.map((job) => (
            <Link
              to={`/hr/my-jobs/${job.id}`}
              key={job.id}
              className="block bg-white p-6 border rounded-xl shadow-sm hover:shadow-md hover:border-red-300 transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="p-3 bg-red-50 rounded-lg">
                  <Briefcase className="w-6 h-6 text-red-800" />
                </div>
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {formatDate(job.createdAt)}
                </span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                {job.jobTitle}
              </h3>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center capitalize">
                  <Briefcase className="w-4 h-4 mr-2 text-gray-400" />
                  {job.jobPosition}
                </div>
                <div className="flex items-center">
                  <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                  {job.experience} years experience
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyJobs;
